import type { FacilityScoreRun } from '../index.js'
import { formatScore } from '../../core/index.js'
import { useRecalculateFacilityScores } from '../index.js'
import { TIER_LABELS } from '../../core/index.js'
import { Button } from '../../core/index.js'
import { RefreshCw } from 'lucide-react'

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function ScoreCell({ value }: { value: number | null }) {
  return <span className={value === null ? 'text-ink-mid' : 'font-bold text-ink'}>{value === null ? '—' : formatScore(value)}</span>
}

/** The run's result: how many moved which way, then every worker's score before and after. */
export function ScoreRunResult({ run }: { run: FacilityScoreRun }) {
  const { summary } = run
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-mid">
        <span>
          <span className="font-bold text-ink">{summary.scored}</span> scored, <span className="font-bold text-ink">{summary.unscored}</span> with nothing to score
        </span>
        <span>
          <span className="font-bold text-green">{summary.up} up</span> · <span className="font-bold text-coral">{summary.down} down</span> · {summary.unchanged} unchanged
        </span>
        <span>
          {run.period.from.slice(0, 7)} · computed {formatDateTime(run.computedAt)}
        </span>
      </div>
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-left text-[9px] font-bold uppercase tracking-wide text-ink-mid">
            <th className="py-1 pr-2 font-bold">Worker</th>
            <th className="py-1 pr-2 font-bold">Tier</th>
            <th className="py-1 pr-2 font-bold text-right">Before</th>
            <th className="py-1 pr-2 font-bold text-right">After</th>
            <th className="py-1 font-bold"></th>
          </tr>
        </thead>
        <tbody>
          {run.workers.map((worker) => (
            <tr key={worker.workerId} className="border-t border-marine/10">
              <td className="py-1.5 pr-2 text-ink">{worker.name}</td>
              <td className="py-1.5 pr-2 text-ink-mid">{TIER_LABELS[worker.tier]}</td>
              <td className="py-1.5 pr-2 text-right">
                <ScoreCell value={worker.before} />
              </td>
              <td className="py-1.5 pr-2 text-right">
                <ScoreCell value={worker.after} />
              </td>
              <td className="py-1.5 text-[10px] text-ink-mid">{worker.after === null ? 'nothing to score' : worker.provisional ? 'provisional' : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Recomputes every worker's running score at the facility on demand, and
 * shows what moved. The engine also runs after every event; this is for
 * seeing the effect of a configuration change, or catching up a roster.
 */
export function RecalculateScoresCard({ facilityId, hasWorkers = true }: { facilityId: string; /** False for an empty roster, which leaves nothing to recompute. */ hasWorkers?: boolean }) {
  const recalculate = useRecalculateFacilityScores(facilityId)

  return (
    <div className="bg-white border border-marine/15 rounded p-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-black text-ink">Scores</div>
          <p className="text-[11px] text-ink-mid max-w-xl leading-relaxed">
            Every worker's running score for this period, recomputed from their outcomes with the behaviors and scoring rules as configured now. Tiers do
            not change until the period closes.
          </p>
        </div>
        <Button icon={RefreshCw} disabled={recalculate.isPending || !hasWorkers} title={hasWorkers ? undefined : 'No workers on the roster yet'} onClick={() => recalculate.mutate()}>
          {recalculate.isPending ? 'Recalculating…' : 'Recalculate scores'}
        </Button>
      </div>
      {!hasWorkers && <p className="text-[11px] text-ink-mid">No workers on the roster yet, so there is nothing to score.</p>}
      {recalculate.isError && (
        <p role="alert" className="text-xs font-bold text-coral">
          {recalculate.error instanceof Error && recalculate.error.message ? recalculate.error.message : 'The recalculation failed.'}
        </p>
      )}
      {recalculate.data && <ScoreRunResult run={recalculate.data} />}
    </div>
  )
}
